import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, X, Target } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000; // 1 minute

function ScenarioAlertsBanner({ currency, onViewScenario }) {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const notifiedRef = useRef(new Set());

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/scenarios/alerts?currency=${currency}`, {
        credentials: 'include'
      });
      if (!res.ok) return;
      const data = await res.json();
      const newAlerts = data.alerts || [];
      setAlerts(newAlerts);

      // Fire toast + browser notification ONLY for fresh alerts.
      // The banner persists for all matching alerts even after cooldown.
      newAlerts.forEach((a) => {
        if (!a.fresh) return;
        if (notifiedRef.current.has(a.scenario_id)) return;
        notifiedRef.current.add(a.scenario_id);
        const first = a.triggered_steps[0];
        if (!first) return;
        const title = `Target reached: ${a.name}`;
        const body = first.message;
        toast.info(`${title} — ${body}`, { duration: 7000 });
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification(title, { body, tag: `scn-${a.scenario_id}` });
          } catch (err) {
            // Browser may block notifications — non-critical
            console.debug('Notification suppressed:', err?.message);
          }
        }
      });
    } catch (err) {
      console.error('fetchAlerts failed:', err);
    }
  }, [currency]);

  useEffect(() => {
    // Reset dismissed and notified tracking when currency changes
    setDismissed(new Set());
    notifiedRef.current = new Set();
    fetchAlerts();
    const interval = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    const handleFocus = () => fetchAlerts();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchAlerts]);

  const visibleAlerts = alerts.filter((a) => !dismissed.has(a.scenario_id));
  if (visibleAlerts.length === 0) return null;

  const dismissAlert = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="scenario-alerts-banner" className="space-y-2 mb-6">
      {visibleAlerts.map((alert) => (
        <Card
          key={alert.scenario_id}
          data-testid={`scenario-alert-${alert.scenario_id}`}
          className="border-accent/40 bg-accent/5"
        >
          <CardContent className="p-4 flex items-start gap-3">
            <div className="p-2 bg-accent/10 rounded-md shrink-0">
              <Target className="h-4 w-4 text-accent" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-sm font-semibold">{alert.name}</p>
                <Badge className="bg-accent/10 text-accent border-accent/20 text-xs">
                  <Bell className="h-3 w-3 mr-1" strokeWidth={1.8} /> Target reached
                </Badge>
              </div>
              <ul className="space-y-0.5">
                {alert.triggered_steps.map((step, i) => (
                  <li key={`${alert.scenario_id}-step-${i}-${step.message?.slice(0, 30) || i}`} className="text-xs text-muted-foreground">
                    {step.message}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {onViewScenario && (
                <Button
                  data-testid={`view-alert-${alert.scenario_id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => onViewScenario(alert.scenario_id)}
                  className="h-8"
                >
                  View
                </Button>
              )}
              <Button
                data-testid={`dismiss-alert-${alert.scenario_id}`}
                variant="ghost"
                size="sm"
                onClick={() => dismissAlert(alert.scenario_id)}
                className="h-8 w-8 p-0"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default ScenarioAlertsBanner;
